/*
 *  Product Form
 *    Try to limit the functions & methods in this class to those that
 *    are related to the primary function of a form -  adding a product to the
 *    cart and allowing users to select variants via way of a visual "swatch".
 *    ProductForm can be called as it's own section or instanciated inside
 *    another section's TS.
 *
 *  Version
 *    1.0.0 - 2022/04/10
 */

import {
  CartAdd, cartAdd, cartGetQuantityOfVariant,
  Variant, printMoney, t
} from '@process-creative/slate-theme-tools';
import { Swatches } from '../../objects/product/swatches';
import { ATTR_DISABLED } from "../../tools/constants/attributes";
import { EVT_VARIANT_CHANGED, UpdatedVariant } from './variant-selector';
import { CLASS_INITIALISED, CLASS_DISABLED, CLASS_HIDDEN, CLASS_ACTIVE } from '../../tools/constants/classes';
import { KlaviyoBackInStock, SELECTOR_BIS_CONTAINER } from './product-klaviyo-bis';
import { getSubscriptionProperties , getSubscriptionName } from './subscription-option';
import { QuantitySelectorElement } from '../quantity-selector';
import { SlideDrawer } from '../slide-drawer';
import { totalRevealDuration } from '../../sections/animated-hero-banner'

import '../../bundle-builder/bundle-builder';
import '../../bundle-builder/bundle-builder-option-form';
import '../../personalisation-engine/personalisation-engine';

//Recharge Selectors
export const SUBSCRIPTION_TOGGLE = '[data-subscription-toggle]';
export const SUBSCRIPTION_FREQUENCY_SELECT = '[data-frequency-select]';
export const SUBSCRIPTION_FREQUENCY = '[data-frequency]';
export const SUBSCRIPTION_ADD = '[data-subscription-button-cart]';
export const SUBSCRIPTION_BUTTON_EXISTING = '[data-subscription-button-existing]';
export const SUBSCRIPTION_INFO_TOGGLE = '[data-subscription-info-toggle]';
export const SUBSCRIPTION_INFO = '[data-subscription-info]';
export const VARIANT_SELECTOR_RECHARGE = 'data-subscription-variant-id';
export const SUBSCRIPTION_CONTAINER = '[data-subscription]';
export const DELIVERY_FREQUENCY_CONTAINER = '[data-delivery-frequency]';
export const DEFAULT_PURCHASE_CONTAINER = '[data-purchase-default]';
export const PRODUCT_RECHARGE_MONEY = '[data-recharge-saved]';
export const PRODUCT_RECHARGE_DISCOUNT = '[data-recharge-discount]';
export const RECHARGE_RADIO = '[data-subscription-option-radio]';
export const SEL_FORM_CONTAINER = '[data-product-form]';
export const SEL_ENGRAVE_DRAWER = '[data-engrave-drawer]';
export const SEL_ENGRAVE_COUNT = '[data-engrave-count]';

const SEL_ADD = '[data-product-form-add]';
const SEL_ADD_LABEL = '[data-product-form-add-label]';
const SEL_MONEY = '[data-product-money]';
const SEL_COMPARE = '[data-product-compare-money]';
const SEL_PREORDER_MESSAGE = '[data-preorder-message]';
const SEL_SCROLL_TO_BUTTONS = '[data-scroll-to]';

// Engrave selectors
const SEL_ENGRAVE_INPUT = '[data-engrave-input]';
const SEL_ENGRAVE_BUTTON = '[data-engrave-personalize]';
const SEL_ENGRAVE_REMOVE = '[data-engrave-remove]';
const SEL_ENGRAVE_CONTAINER = '[data-engrave-container]';
const SEL_ADD_ENGRAVE = '[data-add-engrave]';
const SEL_ENGRAVE_PRICE = '[data-engrave-price]';

const ATTR_MAIN_PRODUCT = 'data-main-product';
const ATTR_SCROLL_TO_SECTION = 'data-scroll-to';


let addEngrave = false;
let bundleKey: string;
let totalPrice : number;
let ignoreEngrave :[string] = [''];
export class ProductForm {
  container: HTMLFormElement;
  mainProduct: boolean = false;
  btnAdd: HTMLButtonElement | null = null;
  btnAddLabel: NodeListOf<HTMLSpanElement> | null = null;
  qtySelector: QuantitySelectorElement | null = null;
  form: HTMLFormElement | null = null;
  swatches: Swatches | null = null;
  preorderMessage: HTMLElement | null = null;
  subscriptionContainer : HTMLDivElement | null = null;
  deliveryFrequencyContainer : HTMLDivElement | null =  null;
  defaultPurchaseContainer: HTMLDivElement | null = null;
  lowStockLabel: HTMLDivElement | null = null;
  lowerStockLabel: HTMLSpanElement | null = null;
  itemsLeftLabel: HTMLSpanElement | null = null;
  scrollToButtons: NodeListOf<HTMLDivElement> | null = null;
  private money: NodeListOf<HTMLSpanElement> | null = null;
  private compare: NodeListOf<HTMLSpanElement> | null = null;
  private bisContainer: HTMLElement | null = null;
  private klaviyoBis: KlaviyoBackInStock | null = null;
  private adding = false;
  rechargeMoney:HTMLSpanElement|null = null;
  rechargeDiscount:HTMLSpanElement|null = null;
  rechargeRadioBtn:HTMLInputElement|null = null;

  engraveInput:HTMLInputElement| null = null;
  engraveRemove:HTMLButtonElement | null = null;
  engravePersonalize:HTMLButtonElement | null = null;
  engraveContainer:NodeListOf<HTMLDivElement>| null = null;
  engraveAdd: HTMLButtonElement | null = null;
  engraveDrawer : SlideDrawer |null = null;
  engraveCount : HTMLSpanElement | null = null;
  engravePrice : HTMLButtonElement | null = null;

  private personalisationOptionForm:HTMLElement|null = null;
  private personalisationEngine:HTMLElement | null = null;

  constructor(container: HTMLFormElement) {
    this.container = container;
    this.mainProduct = container.getAttribute(ATTR_MAIN_PRODUCT) === 'true' || false;
    this.queryChildren(container);
    this.attachEvents();
  }

  queryChildren(container: HTMLFormElement) {
    this.btnAdd = container.querySelector(SEL_ADD);
    this.btnAddLabel = container.querySelectorAll(SEL_ADD_LABEL);
    this.money = container.querySelectorAll<HTMLSpanElement>(SEL_MONEY);
    this.compare = container.querySelectorAll<HTMLSpanElement>(SEL_COMPARE);
    this.preorderMessage = container.querySelector<HTMLElement>(SEL_PREORDER_MESSAGE);
    this.bisContainer = container.querySelector(SELECTOR_BIS_CONTAINER);
    this.subscriptionContainer = container.querySelector<HTMLDivElement>(SUBSCRIPTION_CONTAINER);
    this.deliveryFrequencyContainer = container.querySelector<HTMLDivElement>(DELIVERY_FREQUENCY_CONTAINER);
    this.defaultPurchaseContainer = container.querySelector<HTMLDivElement>(DEFAULT_PURCHASE_CONTAINER);
    this.rechargeMoney = container.querySelector(PRODUCT_RECHARGE_MONEY);
    this.rechargeDiscount = container.querySelector(PRODUCT_RECHARGE_DISCOUNT);
    this.rechargeRadioBtn = container.querySelector(RECHARGE_RADIO);
    this.engraveInput= container.querySelector(SEL_ENGRAVE_INPUT);
    this.engravePersonalize = container.querySelector(SEL_ENGRAVE_BUTTON);
    this.engraveRemove = container.querySelector(SEL_ENGRAVE_REMOVE);
    this.engraveContainer = document.querySelectorAll(SEL_ENGRAVE_CONTAINER);
    this.engraveAdd = container.querySelector(SEL_ADD_ENGRAVE);
    this.engraveCount = container.querySelector(SEL_ENGRAVE_COUNT);
    this.engravePrice = container.querySelector(SEL_ENGRAVE_PRICE);
    this.scrollToButtons = document.querySelectorAll<HTMLDivElement>(SEL_SCROLL_TO_BUTTONS);

    this.personalisationOptionForm = container.querySelector('personalisation-option')
    this.personalisationEngine = container.querySelector('personalisation-engine')
  }
  
  onFormSubmit = async (e: Event) => {
    e.preventDefault();
    e.stopPropagation();

    if (!this.swatches) return;

    let { inventory_quantity, inventory_policy, preorder_message } = this.swatches.getVariant();
    let { selling_plan_allocations } : any = this.swatches.getVariant();
    this.adding = true;
    this.btnPending();

    const item = Array.from(new FormData(this.container)).reduce((x, y) => {
      return { ...x, [y[0]]: y[1].toString() };
    }, {} as CartAdd['items'][number]);
    item['quantity'] = this.qtySelector ? this.qtySelector.value : 1;

    if (!preorder_message) preorder_message = t('products.product.preorder');
    if (!inventory_quantity) inventory_quantity = 0;

    //check if preorder
    const preorderProduct = (inventory_quantity <= 0 && inventory_policy == 'continue');

    //Has the user selected a subscription product and is there one?
    const { selected_selling_plan } :any = getSubscriptionProperties(e.currentTarget);
    // Fetching frequency name
    const { selected_plan_name } : any = getSubscriptionName(e.currentTarget);
    
    type Allocation = {
      plan : object
      selling_plan_id:Number
    }
    
    if(selected_selling_plan && selling_plan_allocations) {
      //Does this variant have an allocation for the selected plan frequency?
      if(selling_plan_allocations.some((plan : Allocation) => ()=>{
        plan.selling_plan_id === selected_selling_plan
        }
        )) {
        item.selling_plan = selected_selling_plan;
        //add properties if preorder
      if (selected_selling_plan) {
        item.properties = {
          _plan_name: selected_plan_name
        }
      }
      }
    }

    //add properties if preorder
    if (preorderProduct) {
      item.properties = {
        preorder: preorder_message
      }
    }

    let engravedText = this.engraveInput?.value;
    if(engravedText){
      item.properties = {
        personalised : engravedText,
        "_bundle-key": new Date().getTime().toString()
      }
    }

    let hiddenEngraveItem = this.engraveAdd?.getAttribute('data-add-engrave');
    let engraveObject;
    if(hiddenEngraveItem && engravedText){
      engraveObject = {
        id: parseInt(hiddenEngraveItem),
        quantity:1,
        properties:{
          "_bundle-key": new Date().getTime().toString(),
          "_child-product": "hide"
        }
      }
    }
    // Build item to add to cart then add
    try {
      if(engraveObject && addEngrave){
        await cartAdd({ items: [item,engraveObject] });
      }
      else{
        await cartAdd({ items: [item] });
      }
    } catch (e) {
      console.error(e);
      alert(e);
    }

    this.onVariantChange();
    if (this.qtySelector) this.qtySelector.disabled = false;
    this.adding = false;
  }

  onVariantChange() {
    this.rechargeRadioBtn?.click();
    let discountValue = Number(this.rechargeDiscount?.getAttribute('data-recharge-discount'));
    if (!this.swatches) return;
    const variant = this.swatches.getVariant();

    // checking is there a variant to ignore for personalisation
    this.removePersonalisation();

    // Triggering a click event to remove personalisation on variant change
    this.engraveRemove?.click();

    const {
      inventory_management,
      inventory_policy,
      inventory_quantity,
      price,
      compare_at_price
    } = variant;
    const inCart = cartGetQuantityOfVariant(variant.id);

    if (this.qtySelector) {
      if (inventory_management === 'shopify' && inventory_policy === 'deny') {
        this.qtySelector.max = (inventory_quantity || 0) - inCart;
      } else {
        this.qtySelector.max = null;
      }
    }
    if(addEngrave){
      let variant = this.swatches.getVariant();
      let personalisePrice = this.engravePrice?.getAttribute('data-engrave-price');
      if(personalisePrice){
        totalPrice =  variant.price + parseInt(personalisePrice);
      }
      this.updatePriceElements(totalPrice, compare_at_price);
    }
    else{
      this.updatePriceElements(price, compare_at_price);
    }
    this.updateAddToCart(variant);
    this.updatePreorderMessage(variant);
  }

  /**
   * Updates elements displaying price based on selected variant
   * @param price variant price
   * @param comparePrice variant compare at price
   */
  updatePriceElements(price: number, comparePrice?: number | null, discountValue?:number|null) {
    
    const caculatedPrice = discountValue ? (price) - (price * discountValue) : price;
    if(this.rechargeMoney){
      discountValue?this.rechargeMoney.innerText = printMoney(caculatedPrice):'';
    }
    if (this.money) {
    discountValue?this.money[0].innerText = printMoney(caculatedPrice):this.money.forEach(el => el.innerText = printMoney(caculatedPrice));
    }
    if (this.money) {
      this.money.forEach(el => el.innerText = printMoney(caculatedPrice));
    }
    if (comparePrice && this.compare) {
      this.compare.forEach(el => el.innerText = printMoney(comparePrice));
    }
  }

  /**
   * Updates Add to cart button label based on selected variant
   * @param variant - variant object
   */
  updateAddToCart(variant: Variant) {
    if (!this.btnAdd || !this.btnAddLabel) return;

    // Adjust the add to cart button based on QTY available and in cart
    const inCart = cartGetQuantityOfVariant(variant.id);
    const {
      available,
      inventory_policy,
      inventory_management,
      inventory_quantity
    } = variant;


    if (available) {
      if (
        inventory_policy != 'continue' &&
        inventory_management != null &&
        ((inventory_quantity || 0) - inCart) <= 0
      ) {
        this.btnAllInCart();
      } else if (
        inventory_policy == 'continue' &&
        inventory_management != null &&
        ((inventory_quantity || 0)) <= 0
      ) {
        this.btnPreorder();
      } else {
        this.btnAvailable();
      }
    } else {
      this.btnSoldOut();
    }
  }

  updatePreorderMessage(variant: UpdatedVariant) {

    if (variant.preorder_message && this.preorderMessage) {
      this.preorderMessage.innerHTML = variant.preorder_message;
    } else if (this.preorderMessage) {
      this.preorderMessage.innerHTML = '';
    }
  }

  /** 
   * Enables button and updates label
   */
  btnAvailable() {
    if (!this.btnAdd || !this.btnAddLabel) return;
    this.btnAdd.classList.remove(CLASS_DISABLED);
    this.btnAdd.disabled = false;
    this.btnAddLabel.forEach(btn => btn.innerText = t('products.product.add_to_cart'));
  }

  /** 
   * Shows preorder state for button
   */
  btnPreorder(preorder: boolean = false) {
    if (!this.btnAdd || !this.btnAddLabel) return;
    this.btnAdd.classList.remove(CLASS_DISABLED);
    this.btnAdd.disabled = false;
    this.btnAddLabel.forEach(btn => btn.innerText = t('products.product.preorder'));

    //Show the preorder message, update its value
  }

  /**
   * Updates button label if all items are in the cart
   */
  btnAllInCart() {
    if (!this.btnAdd || !this.btnAddLabel) return;
    this.btnAdd.classList.add(CLASS_DISABLED);
    this.btnAdd.setAttribute(ATTR_DISABLED, ATTR_DISABLED);
    this.btnAddLabel.forEach(btn => btn.innerText = t('products.product.all_in_cart'));
  }

  /** 
   * Disables ATC if out of stock
   */
  btnSoldOut() {
    if (!this.btnAdd || !this.btnAddLabel) return;
    this.btnAdd.classList.add(CLASS_DISABLED);
    this.btnAdd.setAttribute(ATTR_DISABLED, ATTR_DISABLED);
    this.btnAddLabel.forEach(btn => btn.innerText = t('products.product.sold_out'));
  }

  /** 
   * Updates btn to pending state
   */
  btnPending() {
    if (!this.btnAdd || !this.btnAddLabel) return;
    this.btnAddLabel.forEach(btn => btn.innerText = t('products.product.cart_pending'));
    this.btnAdd.disabled = true;
  }

  removePersonalisation(){
    if(this.swatches){
      const variant = this.swatches.getVariant();

      if (variant) {
        let ignoreVariant = this.engraveAdd?.getAttribute('data-ignore');
        let splitIgnore = ignoreVariant?.split("|");
        splitIgnore?.forEach( variant => {
          let individualVariant = variant.split(":");
          ignoreEngrave.push(individualVariant[1]);
        })
        
          if(ignoreEngrave.includes((variant.id).toString())){
            this.engraveAdd?.classList.add("is-hidden");
          }
          else{
            this.engraveAdd?.classList.remove("is-hidden");
          }
        }
    }
  }

  scrollTo(e:MouseEvent) {
    e.preventDefault();
    e.stopPropagation();
    
    const self = e.target as HTMLElement;
    const scrollToSectionAttr = self.getAttribute(ATTR_SCROLL_TO_SECTION)

    if (scrollToSectionAttr) {
      const scrollToSection = document.querySelector(scrollToSectionAttr);
      
      scrollToSection?.scrollIntoView({ behavior: "smooth" });
    }
  }

  attachEvents() {
    this.swatches = new Swatches(this.container, this.mainProduct);
    this.container.addEventListener(EVT_VARIANT_CHANGED, () => this.onVariantChange());
    this.container.addEventListener('submit', this.onFormSubmit);
    this.btnAdd?.addEventListener('click', this.onFormSubmit);

    // if (this.personalisationEngine) {
    //   this.personalisationEngine.addEventListener('data_update', (e) => {
    //     this.btnAdd.classList.toggle('is-hidden', e.detail.value === '')
    //   })
    // }

    if (this.scrollToButtons) {
      this.showActiveScrollBadges(this.scrollToButtons);

      this.scrollToButtons.forEach(btn => {
        btn.addEventListener('click', (e)=>{
          this.scrollTo(e);
        })
      })
    }

    // Ignore personalisation on specific variant that comes through metafield
    this.removePersonalisation();

    this.subscriptionContainer?.addEventListener('click',(e)=>{
      let discountPercent = Number(this.rechargeDiscount?.getAttribute('data-recharge-discount'));
      if(!this.swatches)return;
      const variant = this.swatches.getVariant();
      this.updatePriceElements(variant.price, variant.compare_at_price,discountPercent);
      this.deliveryFrequencyContainer?.classList.add("is-visible");
    });
    this.defaultPurchaseContainer?.addEventListener('click',()=>{
      if(!this.swatches)return;
      const variant = this.swatches.getVariant();
      this.deliveryFrequencyContainer?.classList.remove("is-visible");
      this.updatePriceElements(variant.price, variant.compare_at_price);
    })
    if (this.bisContainer) this.klaviyoBis = new KlaviyoBackInStock(this.bisContainer, this);

    this.engraveInput?.addEventListener('input',()=>{
      if(this.engraveCount){
        this.engraveCount.innerText = `${this.engraveInput?.value.length}/${this.engraveInput?.maxLength}`;
      }
    })

    // Engrave
    this.engravePersonalize?.addEventListener('click',()=>{
      if(this.swatches){
        const variant = this.swatches.getVariant();
      }
      let engravedText = this.engraveInput?.value;
      if(engravedText && this.engraveContainer){
        this.engraveContainer.forEach(container =>{
          container.innerText = `${engravedText}`;
        })
      }
      if(engravedText && this.engraveAdd){
        this.engraveAdd.innerHTML = `
        <div class="o-btn__text">
          <span class="c-product-form__add-btn-label">'${engravedText}'</span>
          <span class="c-product-form__add-btn-separator">—</span>
          <p class="o-pricing c-product-form__pricing o-type--btn">Edit</p>
        </div>
        <div class="o-btn__text">
          <span class="c-product-form__add-btn-label">'${engravedText}'</span>
          <span class="c-product-form__add-btn-separator">—</span>
          <p class="o-pricing c-product-form__pricing o-type--btn">Edit</p>
        </div>`
      }
      addEngrave = true;

      document.querySelectorAll<SlideDrawer>(SEL_ENGRAVE_DRAWER).forEach(e => {
        e.visible = false;
      })

      if(this.btnAdd && this.swatches){
        let variant = this.swatches.getVariant();
        let personalisePrice = this.engravePrice?.getAttribute('data-engrave-price');
        if(personalisePrice){
          totalPrice =  variant.price + parseInt(personalisePrice);
        this.updatePriceElements(totalPrice);
        }
      }
    })
    
    this.removePersonalisationText();

    setTimeout(() => {
      this.revealElements();
    }, totalRevealDuration);
  }

  revealElements() {
    this.scrollToButtons?.forEach(btn => {
      btn.classList.remove(CLASS_DISABLED);
    })
  }

  showActiveScrollBadges(scrollToButtons: NodeListOf<HTMLDivElement>) {
    // Badges are hidden by default, only show it if the section it is set to scroll to when clicked exists
    scrollToButtons.forEach(btn => {
      const scrollToAttr = btn.getAttribute(ATTR_SCROLL_TO_SECTION);
      if (scrollToAttr) {
        const scrollToSection = document.querySelector(scrollToAttr);
        if (scrollToSection) {
          btn.classList.remove(CLASS_HIDDEN);
        }
      }
    })
  }

  removePersonalisationText(){
    this.engraveRemove?.addEventListener('click',()=>{
      if(this.engraveInput && this.engraveContainer){
        this.engraveInput.value='';
        this.engraveContainer.forEach(container => {
          container.innerText = '';
        })
      }
      if(this.engraveCount){
        this.engraveCount.innerText = `${this.engraveInput?.value.length}/${this.engraveInput?.maxLength}`;
      }
      
      if(this.engraveAdd){
        let personalisePrice = this.engravePrice?.getAttribute('data-engrave-price');

        if(personalisePrice == '0'){
          let final = 'Free'

          this.engraveAdd.innerHTML = `
          <div class="o-btn__text">
            <span class="c-product-form__add-btn-label">${t('products.engrave.engrave_button_text')}</span>
            <span class="c-product-form__add-btn-separator">—</span>
            <p class="o-pricing c-product-form__pricing o-type--btn">${ final}</p>
          </div>
          <div class="o-btn__text">
            <span class="c-product-form__add-btn-label">${t('products.engrave.engrave_button_text')}</span>
            <span class="c-product-form__add-btn-separator">—</span>
            <p class="o-pricing c-product-form__pricing o-type--btn">${ final}</p>
          </div>`
        }
        else{
          this.engraveAdd.innerHTML = `
          <div class="o-btn__text">
            <span class="c-product-form__add-btn-label">${t('products.engrave.engrave_button_text')}</span>
            <span class="c-product-form__add-btn-separator">—</span>
            <p class="o-pricing c-product-form__pricing o-type--btn">${printMoney(parseInt(personalisePrice!)) }</p>
          </div>
          <div class="o-btn__text">
            <span class="c-product-form__add-btn-label">${t('products.engrave.engrave_button_text')}</span>
            <span class="c-product-form__add-btn-separator">—</span>
            <p class="o-pricing c-product-form__pricing o-type--btn">${printMoney(parseInt(personalisePrice!)) }</p>
          </div>`
        }
      }

      if(this.btnAdd && this.swatches){
        let variant = this.swatches.getVariant();
        if(variant.price){
        this.updatePriceElements(variant.price);
        }
      }
      addEngrave = false;
    })
  }

  detatchEvents() {
    this.swatches?.dispose();
    this.container.removeEventListener('submit', this.onFormSubmit);
    this.container.removeEventListener(EVT_VARIANT_CHANGED, () => this.onVariantChange());

    this.scrollToButtons?.forEach(e => {
      e.removeEventListener('click', this.scrollTo);
    });
    
    if (this.klaviyoBis) this.klaviyoBis.detatchEvents();
  }
}


/**
 * Initialise the ProductForm class
 * If the product form is moved to its own section you can uncomment
 * this code here.
 *
 */
// document.querySelectorAll<HTMLElement>(SEL_FORM_CONTAINER).forEach(form => {

//   if (form.classList.contains(CLASS_INITIALISED)) return;
//   form.classList.add(CLASS_INITIALISED);

//   new ProductForm(form);
// });

